import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Form, Input, Popconfirm, Skeleton, Tabs, Tag } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { FiArrowLeft, FiExternalLink, FiPlus, FiUpload, FiX } from 'react-icons/fi';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import PermissionGate from '../../../components/auth/PermissionGate';
import BankMismatchReviewPanel from '../../../components/bank/BankMismatchReviewPanel';
import BankManualUploadForm from '../../../components/bank/BankManualUploadForm';
import BankRequestSubmittedModal from '../../../components/bank/BankRequestSubmittedModal';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';
import ifaService from '../../../services/ifaService';
import supplierProfileService from '../../../services/supplierProfileService';
import SupplierTransactionsTab from './_blocks/SupplierTransactionsTab';
import {
  SUPPLIER_PROFILE_TAB_KEYS,
  canViewSupplierProfileTab,
  formatSupplierOnboardingStep,
  getAllowedSupplierProfileTabs,
  resolveSupplierProfileTabFromParams,
} from './commons/supplierProfile.helpers';
import {
  DEMAT_FORMAT_HINT,
  DEMAT_MAX_LENGTH,
  formatDematInput,
  getDematInputError,
  validateDematNumber,
} from '../../../utils/dematValidation';
import {
  applyProfileBankManualUpload,
  buildBankManualUploadState,
  getProfileAttemptedBankFields,
  resetProfileBankAddForm,
} from '../../../utils/bankVerifyManualUpload';

const TAB_KEYS = SUPPLIER_PROFILE_TAB_KEYS;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const DEMAT_CMR_MAX_SIZE = 2 * 1024 * 1024;
const DEMAT_CMR_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const DEMAT_CMR_ALLOWED_EXTENSIONS = /\.(pdf|jpg|jpeg|png|webp)$/i;

const safeValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return value;
};

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—');

const InfoGrid = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map((item) => (
      <div key={item.label} className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{item.label}</p>
        <p className="text-sm text-gray-900 font-semibold mt-1 wrap-break-word">{safeValue(item.value)}</p>
      </div>
    ))}
  </div>
);

export default function SupplierProfilePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isSuperAdmin } = useAuth();

  const supplierUuid = id ? String(id).trim() : '';
  const tabFromUrl = searchParams.get('tab')?.trim() || '';
  const sectionFromUrl = searchParams.get('section')?.trim() || '';
  const allowedTabs = useMemo(
    () => getAllowedSupplierProfileTabs(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin]
  );
  const resolvedTabFromUrl = useMemo(
    () =>
      resolveSupplierProfileTabFromParams({
        tab: tabFromUrl,
        section: sectionFromUrl,
        hasPermission,
        isSuperAdmin,
      }),
    [tabFromUrl, sectionFromUrl, hasPermission, isSuperAdmin]
  );

  const canAddBank = isSuperAdmin || hasPermission(PERMISSIONS.SUPPLIERS_PROFILE_BANK_ADD);
  const canAddDemat = isSuperAdmin || hasPermission(PERMISSIONS.SUPPLIERS_PROFILE_DEMAT_ADD);
  const canSetPrimaryBank = isSuperAdmin || hasPermission(PERMISSIONS.SUPPLIERS_PROFILE_BANK_SET_PRIMARY);
  const canManageOnboarding = isSuperAdmin || hasPermission(PERMISSIONS.SUPPLIERS_MANAGE);
  const canVerifyBankMismatch = isSuperAdmin || hasPermission(PERMISSIONS.RBAC_USER_MANAGEMENT_EDIT);

  const [activeTab, setActiveTab] = useState(resolvedTabFromUrl);
  const [supplier, setSupplier] = useState(null);
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabError, setTabError] = useState({});
  const [loadedTabs, setLoadedTabs] = useState({});
  const [bankForm] = Form.useForm();
  const [dematForm] = Form.useForm();
  const [isBankFormOpen, setIsBankFormOpen] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [bankIfscLookupLoading, setBankIfscLookupLoading] = useState(false);
  const [bankIfscDetails, setBankIfscDetails] = useState(null);
  const [bankIfscError, setBankIfscError] = useState('');
  const [settingPrimaryBankUuid, setSettingPrimaryBankUuid] = useState('');
  const [deletingBankUuid, setDeletingBankUuid] = useState('');
  const [isDematFormOpen, setIsDematFormOpen] = useState(false);
  const [savingDemat, setSavingDemat] = useState(false);
  const [dematCmrFile, setDematCmrFile] = useState(null);
  const [bankMismatchReview, setBankMismatchReview] = useState(null);
  const [bankMismatchLoading, setBankMismatchLoading] = useState(false);
  const [bankMismatchError, setBankMismatchError] = useState('');
  const [bankMismatchActionLoading, setBankMismatchActionLoading] = useState(false);
  const [bankManualUploadData, setBankManualUploadData] = useState(null);
  const [bankSubmitSuccessOpen, setBankSubmitSuccessOpen] = useState(false);
  const [bankSubmitSuccessMessage, setBankSubmitSuccessMessage] = useState('');

  const supplierNumericId = supplier?.id ? Number(supplier.id) : null;

  const fetchers = useMemo(
    () => ({
      [TAB_KEYS.personal]: supplierProfileService.getPersonalInfo,
      [TAB_KEYS.banks]: supplierProfileService.getBankDetails,
      [TAB_KEYS.demat]: supplierProfileService.getDematDetails,
    }),
    []
  );

  const ensureTabData = useCallback(
    async (tabKey, force = false) => {
      if (!canViewSupplierProfileTab(tabKey, hasPermission, isSuperAdmin)) return;
      if (tabKey === TAB_KEYS.transactions) {
        if (!force && loadedTabs[tabKey]) return;
        setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
        return;
      }
      if (!force && loadedTabs[tabKey]) return;

      const fetcher = fetchers[tabKey];
      if (!fetcher || !supplierUuid) return;

      setTabLoading((prev) => ({ ...prev, [tabKey]: true }));
      setTabError((prev) => ({ ...prev, [tabKey]: '' }));
      try {
        const data = await fetcher(supplierUuid);
        setTabData((prev) => ({ ...prev, [tabKey]: data }));
        setLoadedTabs((prev) => ({ ...prev, [tabKey]: true }));
        if (data?.supplier) setSupplier(data.supplier);
      } catch (error) {
        setTabError((prev) => ({
          ...prev,
          [tabKey]: error?.response?.data?.message || 'Failed to load tab data.',
        }));
      } finally {
        setTabLoading((prev) => ({ ...prev, [tabKey]: false }));
      }
    },
    [fetchers, hasPermission, isSuperAdmin, loadedTabs, supplierUuid]
  );

  const loadBankMismatchReview = useCallback(async () => {
    if (!supplierUuid) return;
    setBankMismatchLoading(true);
    setBankMismatchError('');
    try {
      const data = await supplierProfileService.getBankMismatchPendingReview(supplierUuid);
      setBankMismatchReview(data);
    } catch (error) {
      setBankMismatchReview(null);
      setBankMismatchError(error?.response?.data?.message || 'Failed to load pending bank mismatch review.');
    } finally {
      setBankMismatchLoading(false);
    }
  }, [supplierUuid]);

  useEffect(() => {
    if (!supplierUuid) return;
    setActiveTab(resolvedTabFromUrl);
    ensureTabData(resolvedTabFromUrl);
  }, [supplierUuid, resolvedTabFromUrl, ensureTabData]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.banks || !loadedTabs[TAB_KEYS.banks]) return;
    loadBankMismatchReview();
  }, [activeTab, loadedTabs, loadBankMismatchReview]);

  useEffect(() => {
    if (!supplierUuid) return;
    const hasSectionOnly = sectionFromUrl && !tabFromUrl;
    const hasInvalidTab = tabFromUrl && tabFromUrl !== resolvedTabFromUrl;
    if (!hasSectionOnly && !hasInvalidTab) return;
    const params = new URLSearchParams();
    if (resolvedTabFromUrl !== allowedTabs[0]) params.set('tab', resolvedTabFromUrl);
    setSearchParams(params, { replace: true });
  }, [supplierUuid, tabFromUrl, sectionFromUrl, resolvedTabFromUrl, allowedTabs, setSearchParams]);

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    ensureTabData(nextTab);
    const params = new URLSearchParams();
    if (nextTab !== allowedTabs[0]) params.set('tab', nextTab);
    setSearchParams(params, { replace: true });
  };

  const handleAddBankAccount = async (values) => {
    if (!supplierUuid || !canAddBank) return;
    setSavingBank(true);
    try {
      const response = await supplierProfileService.addBankAccount(supplierUuid, {
        account_number: String(values.account_number || '').trim(),
        ifsc_code: String(values.ifsc_code || '').trim().toUpperCase(),
        bank_name: bankIfscDetails?.bank_name || undefined,
        branch_name: bankIfscDetails?.branch_name || undefined,
      });
      if (response?.success === false) {
        const attempted = getProfileAttemptedBankFields(bankForm, bankIfscDetails);
        const manualUpload = buildBankManualUploadState(response, attempted);
        applyProfileBankManualUpload(manualUpload, {
          bankForm,
          setBankIfscDetails,
          setManualUploadData: setBankManualUploadData,
          setBankIfscError,
        });
        return;
      }
      toast.success(response?.message || 'Bank account added successfully.');
      resetProfileBankAddForm({
        bankForm,
        setBankIfscDetails,
        setBankIfscError,
        setManualUploadData: setBankManualUploadData,
        setIsBankFormOpen,
      });
      await ensureTabData(TAB_KEYS.banks, true);
      await loadBankMismatchReview();
    } catch (error) {
      const attempted = getProfileAttemptedBankFields(bankForm, bankIfscDetails);
      const manualUpload = buildBankManualUploadState(error, attempted);
      applyProfileBankManualUpload(manualUpload, {
        bankForm,
        setBankIfscDetails,
        setManualUploadData: setBankManualUploadData,
        setBankIfscError,
      });
    } finally {
      setSavingBank(false);
    }
  };

  const handleManualUploadSubmit = async ({
    accountNumber,
    ifscCode,
    bankName,
    branchName,
    chequeFile,
    otherDocFile,
  }) => {
    if (!supplierUuid || !canAddBank) return;
    setSavingBank(true);
    try {
      const fd = new FormData();
      fd.append('account_number', accountNumber);
      fd.append('ifsc_code', ifscCode);
      fd.append('bank_name', bankName);
      fd.append('branch_name', branchName || '');
      fd.append('cheque', chequeFile);
      if (otherDocFile) fd.append('supporting_document', otherDocFile);
      const response = await supplierProfileService.uploadBankMismatchCheque(supplierUuid, fd);
      resetProfileBankAddForm({
        bankForm,
        setBankIfscDetails,
        setBankIfscError,
        setManualUploadData: setBankManualUploadData,
        setIsBankFormOpen,
      });
      setBankSubmitSuccessMessage(response?.message || '');
      setBankSubmitSuccessOpen(true);
      await ensureTabData(TAB_KEYS.banks, true);
      await loadBankMismatchReview();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to upload documents.');
    } finally {
      setSavingBank(false);
    }
  };

  const handleManualUploadCancel = () => {
    setBankManualUploadData(null);
    bankForm.resetFields();
    setBankIfscDetails(null);
    setBankIfscError('');
  };

  const handleBankIfscChange = async (value) => {
    const nextIfsc = String(value || '').trim().toUpperCase();
    setBankIfscDetails(null);
    setBankIfscError('');
    bankForm.setFieldValue('ifsc_code', nextIfsc);

    if (!IFSC_REGEX.test(nextIfsc)) {
      setBankIfscLookupLoading(false);
      return;
    }

    setBankIfscLookupLoading(true);
    try {
      const response = await ifaService.lookupIFSC(nextIfsc);
      if (bankForm.getFieldValue('ifsc_code') !== nextIfsc) return;
      const details = response?.data || {};
      setBankIfscDetails({
        bank_name: details.bank_name || '',
        branch_name: details.branch_name || '',
        address: details.address || '',
      });
    } catch (error) {
      if (bankForm.getFieldValue('ifsc_code') !== nextIfsc) return;
      setBankIfscDetails(null);
      setBankIfscError(error?.response?.data?.message || 'Could not fetch bank details for this IFSC.');
    } finally {
      if (bankForm.getFieldValue('ifsc_code') === nextIfsc) {
        setBankIfscLookupLoading(false);
      }
    }
  };

  const handleSetPrimaryBank = async (bankUuid) => {
    if (!supplierUuid || !canSetPrimaryBank || !bankUuid) return;
    setSettingPrimaryBankUuid(bankUuid);
    try {
      const response = await supplierProfileService.setPrimaryBankAccount(supplierUuid, bankUuid);
      toast.success(response?.message || 'Primary bank account updated successfully.');
      await ensureTabData(TAB_KEYS.banks, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to set primary bank account.');
    } finally {
      setSettingPrimaryBankUuid('');
    }
  };

  const handleDeleteBankAccount = async (bankUuid) => {
    if (!supplierUuid || !canAddBank || !bankUuid) return;
    setDeletingBankUuid(bankUuid);
    try {
      const response = await supplierProfileService.deleteBankAccount(supplierUuid, bankUuid);
      toast.success(response?.message || 'Bank account removed successfully.');
      await ensureTabData(TAB_KEYS.banks, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove bank account.');
    } finally {
      setDeletingBankUuid('');
    }
  };

  const handleBankMismatchVerify = async (status) => {
    const documentId = bankMismatchReview?.pending_review?.cheque_document?.document_id;
    if (!documentId || !supplierUuid) return;
    setBankMismatchActionLoading(true);
    try {
      await supplierProfileService.verifyBankMismatchDocument(supplierUuid, documentId, status);
      toast.success(status === 'VERIFIED' ? 'Cheque approved.' : 'Cheque rejected.');
      await ensureTabData(TAB_KEYS.banks, true);
      await loadBankMismatchReview();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update document status.');
    } finally {
      setBankMismatchActionLoading(false);
    }
  };

  const handleDematCmrFileChange = (file) => {
    if (!file) return;
    if (file.size > DEMAT_CMR_MAX_SIZE) {
      toast.error('CMR document size must be under 2 MB.');
      return;
    }
    if (!DEMAT_CMR_ALLOWED_TYPES.includes(file.type) && !DEMAT_CMR_ALLOWED_EXTENSIONS.test(file.name || '')) {
      toast.error('Only PDF, JPG, JPEG, PNG, or WebP files are allowed.');
      return;
    }
    setDematCmrFile(file);
  };

  const handleAddDematAccount = async (values) => {
    if (!supplierUuid || !canAddDemat) return;
    if (!dematCmrFile) {
      toast.error('Please upload a CMR document.');
      return;
    }

    const dematCheck = validateDematNumber(values.demat_number);
    if (!dematCheck.valid) {
      toast.error(dematCheck.message || 'Enter a valid demat number.');
      return;
    }

    const dematNumber = String(values.demat_number || '').trim().toUpperCase();
    const formData = new FormData();
    formData.append('demat_number', dematNumber);
    formData.append('file', dematCmrFile, dematCmrFile.name || 'cmr-document');

    setSavingDemat(true);
    try {
      const response = await supplierProfileService.uploadDematCmr(supplierUuid, formData);
      toast.success(response?.message || 'Demat account added successfully.');
      dematForm.resetFields();
      setDematCmrFile(null);
      setIsDematFormOpen(false);
      await ensureTabData(TAB_KEYS.demat, true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add demat account.');
    } finally {
      setSavingDemat(false);
    }
  };

  const renderTabBody = (tabKey, content) => {
    if (tabLoading[tabKey]) {
      return (
        <Card className="rounded-xl">
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      );
    }
    if (tabError[tabKey]) {
      return (
        <Alert
          type="error"
          showIcon
          message={tabError[tabKey]}
          action={
            <Button size="small" onClick={() => ensureTabData(tabKey, true)}>
              Retry
            </Button>
          }
        />
      );
    }
    return content;
  };

  const personalData = tabData[TAB_KEYS.personal] || {};
  const bankData = tabData[TAB_KEYS.banks] || {};
  const dematData = tabData[TAB_KEYS.demat] || {};
  const bankAccounts = (bankData.bank_details || []).filter((bank) => bank.is_active !== false);
  const dematAccounts = dematData.demat_details || [];
  const personalInfo = personalData.personal_info || {};
  const headerSupplier = supplier || personalData.supplier || bankData.supplier || dematData.supplier;

  const onboardingStatusTag = useMemo(() => {
    const step = headerSupplier?.supplier_onboarding_step;
    if (step === 'COMPLETED') return <Tag color="green">Onboarding Complete</Tag>;
    if (step) return <Tag color="orange">{formatSupplierOnboardingStep(step)}</Tag>;
    return null;
  }, [headerSupplier?.supplier_onboarding_step]);

  const tabItems = useMemo(() => {
    const items = [];

    if (canViewSupplierProfileTab(TAB_KEYS.personal, hasPermission, isSuperAdmin)) {
      items.push({
        key: TAB_KEYS.personal,
        label: 'Personal Info',
        children: renderTabBody(
          TAB_KEYS.personal,
          <Card className="rounded-xl" title="Personal Details">
            <InfoGrid
              items={[
                { label: 'Full Name', value: headerSupplier?.name },
                { label: 'Supplier ID', value: headerSupplier?.supplier_id },
                { label: 'PAN', value: headerSupplier?.pan },
                { label: 'Email', value: headerSupplier?.email },
                { label: 'Mobile', value: headerSupplier?.mobile },
                { label: 'Date of Birth', value: formatDate(personalInfo.dob) },
                { label: 'Gender', value: personalInfo.gender },
                { label: 'Entity Type', value: personalInfo.entity_type },
                { label: 'Onboarding Step', value: formatSupplierOnboardingStep(headerSupplier?.supplier_onboarding_step) },
                { label: 'Created On', value: formatDate(headerSupplier?.created_at) },
              ]}
            />
          </Card>
        ),
      });
    }

    if (canViewSupplierProfileTab(TAB_KEYS.banks, hasPermission, isSuperAdmin)) {
      items.push({
        key: TAB_KEYS.banks,
        label: 'Bank Details',
        children: renderTabBody(
          TAB_KEYS.banks,
          <Card
            className="rounded-xl"
            title="Bank Accounts"
            extra={
              <PermissionGate permission={PERMISSIONS.SUPPLIERS_PROFILE_BANK_ADD}>
                <Button
                  type={isBankFormOpen ? 'default' : 'primary'}
                  icon={isBankFormOpen ? <FiX /> : <FiPlus />}
                  onClick={() => {
                    if (isBankFormOpen) {
                      resetProfileBankAddForm({
                        bankForm,
                        setBankIfscDetails,
                        setBankIfscError,
                        setManualUploadData: setBankManualUploadData,
                        setIsBankFormOpen,
                      });
                    } else {
                      setIsBankFormOpen(true);
                    }
                  }}
                >
                  {isBankFormOpen ? 'Cancel' : 'Add Bank'}
                </Button>
              </PermissionGate>
            }
          >
            <div className="flex flex-col gap-6">
              <BankMismatchReviewPanel
                reviewData={bankMismatchReview}
                loading={bankMismatchLoading}
                error={bankMismatchError}
                actionLoading={bankMismatchActionLoading}
                canAct={canVerifyBankMismatch}
                onVerify={handleBankMismatchVerify}
              />

              {canAddBank && isBankFormOpen ? (
                <div className="rounded-xl border border-green-100 bg-green-50/30 p-4">
                  {bankManualUploadData ? (
                    <BankManualUploadForm
                      manualUploadData={bankManualUploadData}
                      loading={savingBank}
                      onSubmit={handleManualUploadSubmit}
                      onCancel={handleManualUploadCancel}
                    />
                  ) : (
                  <Form form={bankForm} layout="vertical" onFinish={handleAddBankAccount}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                      <Form.Item
                        label="Account Number"
                        name="account_number"
                        rules={[{ required: true, message: 'Enter account number.' }]}
                      >
                        <Input placeholder="Enter bank account number" disabled={savingBank} />
                      </Form.Item>
                      <Form.Item
                        label="IFSC Code"
                        name="ifsc_code"
                        normalize={(value) => String(value || '').toUpperCase()}
                        rules={[
                          { required: true, message: 'Enter IFSC code.' },
                          { pattern: IFSC_REGEX, message: 'Enter a valid IFSC code.' },
                        ]}
                      >
                        <Input
                          placeholder="HDFC0001234"
                          maxLength={11}
                          disabled={savingBank}
                          onChange={(event) => handleBankIfscChange(event.target.value)}
                        />
                      </Form.Item>
                    </div>

                    {bankIfscLookupLoading ? (
                      <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                        Fetching bank details...
                      </div>
                    ) : null}
                    {bankIfscDetails ? (
                      <div className="mb-4 rounded-lg border border-green-100 bg-white px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">IFSC Bank Details</p>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <p><span className="text-gray-500">Bank:</span> {safeValue(bankIfscDetails.bank_name)}</p>
                          <p><span className="text-gray-500">Branch:</span> {safeValue(bankIfscDetails.branch_name)}</p>
                        </div>
                      </div>
                    ) : null}
                    {bankIfscError ? (
                      <Alert type="error" showIcon message={bankIfscError} className="mb-4" />
                    ) : null}

                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => {
                          resetProfileBankAddForm({
                            bankForm,
                            setBankIfscDetails,
                            setBankIfscError,
                            setManualUploadData: setBankManualUploadData,
                            setIsBankFormOpen,
                          });
                        }}
                        disabled={savingBank}
                      >
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit" loading={savingBank}>
                        Add Bank Account
                      </Button>
                    </div>
                  </Form>
                  )}
                </div>
              ) : null}

              {bankAccounts.length === 0 ? (
                <Empty description="No bank accounts linked." />
              ) : (
                <div className="space-y-3">
                  {bankAccounts.map((bank) => (
                    <div
                      key={bank.uuid || bank.id}
                      className="rounded-xl border border-gray-200 p-4 bg-gray-50/40"
                    >
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{safeValue(bank.bank_name)}</p>
                          {bank.is_primary ? (
                            <Tag color="green" className="m-0 font-medium">Primary</Tag>
                          ) : null}
                          {bank.is_verified ? (
                            <Tag color="blue" className="m-0">Verified</Tag>
                          ) : (
                            <Tag className="m-0">Unverified</Tag>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {canSetPrimaryBank && !bank.is_primary && bank.is_verified && bank.uuid ? (
                            <Popconfirm
                              title="Set as primary bank account?"
                              description="This will demote the current primary account."
                              okText="Set Primary"
                              onConfirm={() => handleSetPrimaryBank(bank.uuid)}
                            >
                              <Button
                                size="small"
                                loading={settingPrimaryBankUuid === bank.uuid}
                                disabled={Boolean(
                                  (settingPrimaryBankUuid && settingPrimaryBankUuid !== bank.uuid) ||
                                    deletingBankUuid
                                )}
                              >
                                Set as Primary
                              </Button>
                            </Popconfirm>
                          ) : null}
                          {canAddBank && bank.uuid ? (
                            <Popconfirm
                              title="Remove this bank account?"
                              description="This will deactivate the bank account."
                              okText="Remove"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => handleDeleteBankAccount(bank.uuid)}
                            >
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                loading={deletingBankUuid === bank.uuid}
                                disabled={Boolean(
                                  (deletingBankUuid && deletingBankUuid !== bank.uuid) ||
                                    settingPrimaryBankUuid
                                )}
                              >
                                Delete
                              </Button>
                            </Popconfirm>
                          ) : null}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <p><span className="text-gray-500">Account Number:</span> {safeValue(bank.account_number)}</p>
                        <p><span className="text-gray-500">IFSC:</span> {safeValue(bank.ifsc_code)}</p>
                        <p><span className="text-gray-500">Branch:</span> {safeValue(bank.branch_name)}</p>
                        <p><span className="text-gray-500">Beneficiary:</span> {safeValue(bank.beneficiary_name)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ),
      });
    }

    if (canViewSupplierProfileTab(TAB_KEYS.demat, hasPermission, isSuperAdmin)) {
      items.push({
        key: TAB_KEYS.demat,
        label: 'Demat & Documents',
        children: renderTabBody(
          TAB_KEYS.demat,
          <Card
            className="rounded-xl"
            title="Demat Accounts"
            extra={
              <PermissionGate permission={PERMISSIONS.SUPPLIERS_PROFILE_DEMAT_ADD}>
                <Button
                  type={isDematFormOpen ? 'default' : 'primary'}
                  icon={isDematFormOpen ? <FiX /> : <FiPlus />}
                  onClick={() => {
                    if (isDematFormOpen) {
                      dematForm.resetFields();
                      setDematCmrFile(null);
                    }
                    setIsDematFormOpen((open) => !open);
                  }}
                >
                  {isDematFormOpen ? 'Cancel' : 'Add Demat'}
                </Button>
              </PermissionGate>
            }
          >
            <div className="space-y-4">
              {canAddDemat && isDematFormOpen ? (
                <div className="rounded-xl border border-green-100 bg-green-50/30 p-4">
                  <Form form={dematForm} layout="vertical" onFinish={handleAddDematAccount}>
                    <Form.Item
                      label="Demat Account Number"
                      name="demat_number"
                      normalize={formatDematInput}
                      rules={[
                        { required: true, message: 'Enter demat account number.' },
                        {
                          validator: (_, value) => {
                            const err = getDematInputError(value);
                            return err ? Promise.reject(new Error(err)) : Promise.resolve();
                          },
                        },
                      ]}
                      extra={DEMAT_FORMAT_HINT}
                    >
                      <Input
                        placeholder="Enter 16-digit demat number"
                        maxLength={DEMAT_MAX_LENGTH}
                        disabled={savingDemat}
                      />
                    </Form.Item>

                    <Form.Item label="CMR / CML Document" required>
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3 text-sm text-gray-700 hover:border-green-500 hover:text-green-700">
                        <FiUpload />
                        {dematCmrFile ? dematCmrFile.name : 'Choose PDF or image (max 2 MB)'}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                          disabled={savingDemat}
                          onChange={(event) => {
                            handleDematCmrFileChange(event.target.files?.[0] || null);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    </Form.Item>

                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => {
                          dematForm.resetFields();
                          setDematCmrFile(null);
                          setIsDematFormOpen(false);
                        }}
                        disabled={savingDemat}
                      >
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit" loading={savingDemat}>
                        Add Demat Account
                      </Button>
                    </div>
                  </Form>
                </div>
              ) : null}

              {dematAccounts.length === 0 ? (
                <Empty description="No demat accounts linked." />
              ) : (
                <div className="space-y-3">
                  {dematAccounts.map((demat) => (
                    <div
                      key={demat.uuid || demat.id}
                      className="rounded-xl border border-gray-200 p-4 bg-gray-50/40"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-sm font-semibold text-gray-900">{safeValue(demat.dp_name)}</p>
                        {demat.is_primary ? (
                          <Tag color="green" className="m-0 font-medium">Primary</Tag>
                        ) : null}
                        {demat.is_verified ? (
                          <Tag color="blue" className="m-0">Verified</Tag>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <p><span className="text-gray-500">Demat Number:</span> {safeValue(demat.demat_number)}</p>
                        <p><span className="text-gray-500">DP ID:</span> {safeValue(demat.dp_id)}</p>
                        <p><span className="text-gray-500">Client ID:</span> {safeValue(demat.client_id)}</p>
                      </div>
                      {demat.cmr_document_url ? (
                        <a
                          href={demat.cmr_document_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-blue-600 hover:underline"
                        >
                          <FiExternalLink size={14} />
                          View CMR document
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ),
      });
    }

    if (canViewSupplierProfileTab(TAB_KEYS.transactions, hasPermission, isSuperAdmin)) {
      items.push({
        key: TAB_KEYS.transactions,
        label: 'Transactions',
        children: renderTabBody(
          TAB_KEYS.transactions,
          supplierNumericId ? (
            <SupplierTransactionsTab supplierNumericId={supplierNumericId} />
          ) : (
            <Empty description="Supplier ID not available for transactions." />
          )
        ),
      });
    }

    return items;
  }, [
    bankAccounts,
    bankIfscDetails,
    bankIfscError,
    bankIfscLookupLoading,
    bankMismatchActionLoading,
    bankMismatchError,
    bankMismatchLoading,
    bankMismatchReview,
    canAddBank,
    canAddDemat,
    canSetPrimaryBank,
    canVerifyBankMismatch,
    dematAccounts,
    dematCmrFile,
    headerSupplier,
    hasPermission,
    isBankFormOpen,
    isDematFormOpen,
    isSuperAdmin,
    personalInfo,
    savingBank,
    savingDemat,
    settingPrimaryBankUuid,
    deletingBankUuid,
    supplierNumericId,
    tabError,
    tabLoading,
  ]);

  if (!supplierUuid) {
    return (
      <DashboardPageWrapper>
        <Alert type="error" showIcon message="Invalid supplier ID." />
      </DashboardPageWrapper>
    );
  }

  return (
    <DashboardPageWrapper>
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate('/supplier-masters')}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <FiArrowLeft size={16} />
          Back to Supplier Masters
        </button>
      </div>

      {!headerSupplier && tabLoading[TAB_KEYS.personal] ? (
        <Card className="rounded-2xl mb-6">
          <Skeleton active avatar paragraph={{ rows: 2 }} />
        </Card>
      ) : !headerSupplier && tabError[TAB_KEYS.personal] ? (
        <Alert
          type="error"
          showIcon
          message={tabError[TAB_KEYS.personal]}
          className="mb-6"
          action={
            <Button size="small" onClick={() => ensureTabData(TAB_KEYS.personal, true)}>
              Retry
            </Button>
          }
        />
      ) : headerSupplier ? (
        <Card className="rounded-2xl mb-6 border border-gray-100 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">{safeValue(headerSupplier.name)}</h1>
                {onboardingStatusTag}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                <span><span className="text-gray-500">Supplier ID:</span> {safeValue(headerSupplier.supplier_id)}</span>
                <span><span className="text-gray-500">PAN:</span> {safeValue(headerSupplier.pan)}</span>
                <span><span className="text-gray-500">Email:</span> {safeValue(headerSupplier.email)}</span>
                <span><span className="text-gray-500">Mobile:</span> {safeValue(headerSupplier.mobile)}</span>
              </div>
            </div>

            <PermissionGate permission={PERMISSIONS.SUPPLIERS_MANAGE}>
              {canManageOnboarding ? (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/supplier-masters/onboard/${supplierUuid}?edit=1`)}
                >
                  Edit in Wizard
                </Button>
              ) : null}
            </PermissionGate>
          </div>
        </Card>
      ) : null}

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        className="supplier-profile-tabs"
      />

      <BankRequestSubmittedModal
        open={bankSubmitSuccessOpen}
        message={bankSubmitSuccessMessage}
        onClose={() => {
          setBankSubmitSuccessOpen(false);
          setBankSubmitSuccessMessage('');
        }}
      />
    </DashboardPageWrapper>
  );
}
